// Animate browser snapshots, not the live layout: fixed/sticky controls and
// unsaved forms retain their original DOM, focus and containing blocks.
import { replaySurface } from './surface-motion.js';
import { replayDataSurface } from './data-motion.js';
import { settingsDirection } from './settings-navigation.js';

export function createWorkspaceTransitions({ document: doc = globalThis.document,
  media = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)'), render = () => Promise.resolve() } = {}) {
  let active;
  function cancel(superseded = true) { active?.skip(superseded); }
  function run(update, { direction = 1, commitOnCancel = true, target = doc?.querySelector('#main-content') } = {}) {
    cancel();
    if (!doc?.startViewTransition || media?.matches || doc.hidden || !target
      || target.matches('.immersive') || doc.querySelector('[role="dialog"], dialog[open], .budget-modal-backdrop')) {
      return Promise.resolve().then(update).then(render).then(() => replaySurface(target));
    }
    const root = doc.documentElement;
    const settleEntrance = () => {
      // Capture opaque content. Finishing (rather than toggling CSS animation
      // names) prevents a second fade when the snapshot disappears.
      for (const animation of target.getAnimations?.({ subtree: true }) || []) {
        if (animation.animationName === 'motion-content-in' || /^dashboard-(card|ring|bar)-in(?:-|$)/.test(animation.animationName || '')
          || animation.id === 'nur-surface-enter') animation.finish();
      }
    };
    let started = false, transition, timer, resolve, reject;
    const updated = new Promise((yes, no) => { resolve = yes; reject = no; });
    const perform = () => {
      if (!started) {
        started = true;
        clearTimeout(timer);
        Promise.resolve().then(update).then(render).then(() => { replayDataSurface(target); settleEntrance(); }).then(resolve, reject);
      }
      return updated;
    };
    const entry = { target, get pending() { return !started; }, skip(superseded = false) {
      // A newer tab intent supersedes a local update not yet committed. Router
      // guards, however, must always be released, even when their snapshot skips.
      if (superseded && !commitOnCancel && !started) { started = true; clearTimeout(timer); resolve(); }
      transition?.skipTransition();
      void perform().catch(() => {});
    } };
    active = entry;
    root.setAttribute('data-workspace-transition', direction < 0 ? 'back' : 'forward');
    target.setAttribute('data-transition-surface', '');
    function clean() {
      clearTimeout(timer);
      if (active === entry) {
        active = undefined;
        root.removeAttribute('data-workspace-transition');
      }
      if (active?.target !== target) target.removeAttribute('data-transition-surface');
    }
    try {
      settleEntrance();
      transition = doc.startViewTransition(perform);
      // A skipped snapshot must never prevent the actual navigation/update.
      transition.ready.catch(() => {});
      transition.updateCallbackDone?.catch(() => {});
      transition.finished.then(clean, clean);
      // Hidden tabs / failed captures must not leave a router guard waiting.
      timer = setTimeout(() => entry.skip(), 180);
    } catch {
      clean();
      void perform().catch(() => {});
    }
    return updated;
  }
  const preferenceChanged = () => { if (media?.matches) cancel(false); };
  const visibilityChanged = () => { if (doc?.hidden) cancel(false); };
  media?.addEventListener?.('change', preferenceChanged);
  doc?.addEventListener?.('visibilitychange', visibilityChanged);
  return { run, cancel, get pending() { return Boolean(active?.pending); }, destroy() {
    cancel();
    media?.removeEventListener?.('change', preferenceChanged);
    doc?.removeEventListener?.('visibilitychange', visibilityChanged);
  } };
}

export function workspaceRouteKey(route) {
  // Opening/closing task details must not replay the board's page transition.
  if (route.meta?.viewKey === 'tasks-board') return '/tasks';
  if (route.meta?.viewKey === 'employee-onboarding') return '/employees/onboarding';
  // Dates, search and restaurant filters do not replay the entire workspace.
  const query = route.query || {};
  return route.path === '/settings'
    ? `${route.path}:${query.tab || 'profile'}:${query.section || ''}` : route.path;
}

const routeOrder = ['/', '/chat', '/tasks', '/budget', '/labor-cost', '/invoices', '/inventory',
  '/analytics', '/banking', '/vision', '/reviews', '/journals', '/audit', '/counterparties', '/employees', '/calendar', '/settings'];
export function workspaceDirection(to, from) {
  const index = path => routeOrder.indexOf('/' + path.split('/')[1]);
  if (index(to.path) !== index(from.path)) return index(to.path) < index(from.path) ? -1 : 1;
  if (to.path.startsWith('/labor-cost/') && from.path.startsWith('/labor-cost/')) {
    const sections = ['schedule', 'master', 'calculation', 'operations', 'settings'];
    return sections.indexOf(to.path.split('/')[2]) < sections.indexOf(from.path.split('/')[2]) ? -1 : 1;
  }
  const sections = ['revenue', 'checks', 'dishes', 'temperature', 'employees'];
  if (to.path.startsWith('/analytics/') && from.path.startsWith('/analytics/')) {
    return sections.indexOf(to.path.split('/')[2]) < sections.indexOf(from.path.split('/')[2]) ? -1 : 1;
  }
  if (to.path === '/settings' && from.path === '/settings') {
    return settingsDirection(to.query, from.query);
  }
  return to.path.length < from.path.length ? -1 : 1;
}

export function installWorkspaceNavigation(router, motion, doc = globalThis.document) {
  let pending;
  const release = () => { pending?.finish(); pending = undefined; };
  const removeBefore = router.beforeEach((to, from) => {
    if (workspaceRouteKey(to) !== workspaceRouteKey(from)) { motion.cancel(); release(); }
  });
  const removeResolve = router.beforeResolve((to, from) => {
    if (!from.name || to.meta.public || from.meta.public || to.name === 'chat' || from.name === 'chat'
      || workspaceRouteKey(to) === workspaceRouteKey(from)) return;
    return new Promise(allow => {
      const rendered = new Promise(finish => { pending = { to, finish }; });
      const target = to.path.startsWith('/counterparties') && from.path.startsWith('/counterparties')
        ? doc?.querySelector('.cp-content')
        : to.meta.viewKey === 'inventory' && from.meta.viewKey === 'inventory'
        ? doc?.querySelector('.inventory-workspace-content')
        : to.path.startsWith('/labor-cost/') && from.path.startsWith('/labor-cost/')
        ? doc?.querySelector('.labor-cost-content')
        : to.path === '/settings' && from.path === '/settings'
          ? doc?.querySelector(to.query.tab === from.query.tab ? '.general-settings-body' : '.settings-content') : undefined;
      void motion.run(async () => { allow(); await rendered; }, { direction: workspaceDirection(to, from), ...(target ? { target } : {}) })
        .catch(() => { allow(); if (pending?.to === to) release(); });
    });
  });
  const removeAfter = router.afterEach(to => { if (pending?.to === to) release(); });
  const removeError = router.onError(() => { motion.cancel(); release(); });
  return () => { motion.cancel(); release(); removeBefore(); removeResolve(); removeAfter(); removeError(); };
}
